import { Bell, Globe, Sparkles, ShieldCheck, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MobileMenuButton } from "./responsive-sidebar";
import { useLanguage } from "@/hooks/use-language";
import { useAuth } from "@/hooks/use-auth";

interface HeaderProps {
  onMobileMenuClick: () => void;
}

export default function Header({ onMobileMenuClick }: HeaderProps) {
  const { language, dir, toggleLanguage } = useLanguage();
  const { user } = useAuth();
  const isAr = language === "ar";

  const currentDate = new Date().toLocaleDateString(isAr ? 'ar-AE' : 'en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  const currency = user?.company?.currency || "AED";

  return (
    <header 
      dir={dir} 
      className="relative z-10 shrink-0 flex items-center justify-between h-15 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-zinc-200/80 dark:border-zinc-800/80 px-4 sm:px-6 select-none"
    >
      <div className="flex items-center gap-3">
        <MobileMenuButton onClick={onMobileMenuClick} />
        
        {/* Live operational badge */}
        <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-full bg-zinc-100/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800 text-[11px] font-medium text-zinc-600 dark:text-zinc-300">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-semibold">{currentDate}</span>
          <span className="text-zinc-300 dark:text-zinc-700">•</span>
          <span className="font-bold text-zinc-900 dark:text-zinc-100">{currency}</span>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {/* Language Switcher */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="h-8 px-3 rounded-full bg-zinc-100/80 dark:bg-zinc-900/80 hover:bg-zinc-200/80 dark:hover:bg-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-white flex items-center gap-1.5 transition-all border border-zinc-200/60 dark:border-zinc-800/60"
          data-testid="language-switcher-button"
        >
          <Globe className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
          <span>{isAr ? "English" : "العربية"}</span>
        </Button>

        {/* Notifications */}
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full"
          data-testid="notifications-button"
        >
          <Bell className="h-4 w-4" />
          <span className="absolute top-1.5 right-1.5 rtl:right-auto rtl:left-1.5 h-2 w-2 rounded-full bg-indigo-600" />
        </Button>
      </div>
    </header>
  );
}
